import { computed, ref, type Ref, type EmitFn } from "vue";
import { useStore } from "vuex";
import { EditableType, LegacyEditableTypes } from "hoxton-shared/enums/editables";
import type {
    CSSSettings,
    EditableValue,
    Editable,
    EditableTypeType,
    ResizeSettings,
    NestedResizeSettings
} from "hoxton-types";

import { useEditable } from "@/composables/useEditable";
import { useOverwrite } from "@/composables/useOverwrite";
import { useEditableMethods } from "@/composables/useEditableMethods";

import { type RootState } from "@/store/types";
import { MediaLibraryAction } from "@/store/modules/mediaLibrary";

import { EditableEvent } from "@/enums/editables";
import type { MediaItem } from "@/types/types";

export function useEditableFile({
    type,
    editable,
    groupsAffectingEditable,
    emit
}: {
    type: Ref<EditableTypeType>;
    editable: Ref<Editable>;
    groupsAffectingEditable: Ref<string[]>;
    emit: EmitFn;
}) {
    const store = useStore<RootState>();

    const resizeSettingsModalIsVisible = ref(false);
    const imageToResize = ref<string>("");
    const isEditingGroupResizeSettings = ref(false);
    const isEditingSingleTemplateResizeSettings = ref(false);
    const resizeSettingsToEdit = ref<NestedResizeSettings | null>(null);
    const templateIdsToEditResizeSettingsOf = ref<string[]>([]);

    const { editableTemplates } = useEditable({ editable });
    const { isTemplateOverwrite, getOverwriteMediaItem } = useOverwrite();
    const { update } = useEditableMethods({ emit });

    const fileSelectAction = computed(() => {
        switch (type.value) {
            case LegacyEditableTypes.BackgroundImage:
            case EditableType.Image:
                return MediaLibraryAction.SelectFile;

            case EditableType.Folder:
                return MediaLibraryAction.SelectFolder;

            case EditableType.Video:
                return MediaLibraryAction.SelectFile;

            default:
                return MediaLibraryAction.SelectFile;
        }
    });

    const templatesUsingGroupMediaItem = computed(() => {
        return editableTemplates.value.filter(
            templateId => !isTemplateOverwrite(editable.value, groupsAffectingEditable.value, templateId)
        );
    });

    function toggleResizeSettingsModal() {
        resizeSettingsModalIsVisible.value = !resizeSettingsModalIsVisible.value;
    }

    function showResizeSettingsModal(
        templateIds: string[],
        isEditingGroupResizeSettingsNew: boolean,
        isEditingSingleTemplateResizeSettingsNew: boolean
    ) {
        const mediaItem = isEditingGroupResizeSettingsNew
            ? getOverwriteMediaItem(editable.value, groupsAffectingEditable.value, null)
            : getOverwriteMediaItem(editable.value, groupsAffectingEditable.value, templateIds[0]!);

        imageToResize.value = mediaItem?.url ?? "";
        resizeSettingsToEdit.value = mediaItem?.resizeSettings ?? null;
        isEditingGroupResizeSettings.value = isEditingGroupResizeSettingsNew;
        isEditingSingleTemplateResizeSettings.value = isEditingSingleTemplateResizeSettingsNew;
        templateIdsToEditResizeSettingsOf.value = templateIds;

        toggleResizeSettingsModal();
    }

    async function fileSelected(editable: Editable, groupsAffectingEditable: string[], templateId: string | null) {
        // TODO reduce complexity of function
        const mediaItem = getOverwriteMediaItem(editable, groupsAffectingEditable, templateId);
        const mediaLibraryConfig: {
            instance: { multiple: boolean; type: EditableTypeType };
            folderId?: string;
            fileId?: string;
        } = {
            instance: {
                multiple: false,
                type: type.value
            }
        };

        if (mediaItem) {
            if ("folderId" in mediaItem && typeof mediaItem.folderId === "string") {
                mediaLibraryConfig.folderId = mediaItem.folderId;
            }
            mediaLibraryConfig.fileId = mediaItem.id;
        }

        const selectedFiles = (await store.dispatch(fileSelectAction.value, mediaLibraryConfig)) as Record<
            string,
            MediaItem
        > | null;

        const files = Object.values(selectedFiles ?? {});

        if (!files.length) {
            return;
        }

        const file:
            | (MediaItem & {
                  resizeSettings?:
                      | ResizeSettings
                      | {
                            resizeSettingsByTemplateId: Record<string, ResizeSettings>;
                        };
              })
            | undefined = files.pop();

        if (!file) {
            return;
        }

        if (mediaItem?.id === file.id && mediaItem.resizeSettings && file.resizeSettings) {
            /*
                If we're selecting the same image, we do not want to overwrite
                previously customised resize settings with default resize settings.
            */
            if (templateId && "resizeSettingsByTemplateId" in mediaItem.resizeSettings) {
                if (mediaItem.resizeSettings.resizeSettingsByTemplateId[templateId]) {
                    file.resizeSettings = {
                        ...mediaItem.resizeSettings.resizeSettingsByTemplateId[templateId]
                    };
                } else {
                    file.resizeSettings = { ...mediaItem.resizeSettings };
                    if ("resizeSettingsByTemplateId" in file.resizeSettings) {
                        delete (file.resizeSettings as { resizeSettingsByTemplateId?: Record<string, ResizeSettings> })
                            .resizeSettingsByTemplateId;
                    }
                }
            } else {
                file.resizeSettings = { ...mediaItem.resizeSettings };
            }
        }

        const eventPayload: {
            mediaItem: typeof file;
            templateId?: string | null;
        } = {
            mediaItem: file
        };

        if (templateId) {
            eventPayload.templateId = templateId;
        }

        emit(EditableEvent.Update, eventPayload);
    }

    function setResizeSettings(resizeSettings: CSSSettings) {
        if (isEditingGroupResizeSettings.value) {
            emit(EditableEvent.Update, {
                mediaItem: {
                    ...getOverwriteMediaItem(editable.value, groupsAffectingEditable.value, null),
                    resizeSettings
                }
            });
        } else {
            emit(EditableEvent.Update, {
                mediaItem: {
                    ...getOverwriteMediaItem(
                        editable.value,
                        groupsAffectingEditable.value,
                        templateIdsToEditResizeSettingsOf.value[0]!
                    ),
                    resizeSettings
                },
                templateId: templateIdsToEditResizeSettingsOf.value[0]
            });
        }
    }

    function updateFileEditable(updateData: {
        value: EditableValue;
        mediaItem?: MediaItem;
        templateId?: string | null;
    }) {
        update({
            editable: editable.value,
            updateData: { ...updateData, ...(updateData.mediaItem && { value: updateData.mediaItem.id }) },
            groupsAffectingEditable: groupsAffectingEditable.value,
            templateId: updateData.templateId ?? null
        });
    }

    return {
        resizeSettingsModalIsVisible,
        imageToResize,
        isEditingGroupResizeSettings,
        isEditingSingleTemplateResizeSettings,
        resizeSettingsToEdit,
        templateIdsToEditResizeSettingsOf,
        fileSelectAction,
        templatesUsingGroupMediaItem,
        toggleResizeSettingsModal,
        fileSelected,
        showResizeSettingsModal,
        setResizeSettings,
        updateFileEditable
    };
}
